'use client';
import { useEffect, useRef } from 'react';
import { ETSY_URL, BASE_IMG } from '@/data/bundles';

const featured = [
  { badge: 'Most Popular', category: 'Productivity', catClass: 'cat-productivity', title: '14,700+ Canva & KDP Templates', desc: 'Planners, journals, workbooks — editable KDP interiors ready to publish. A massive professional starter library.', img: `${BASE_IMG}/14700_canva_page_templates_bundle.jpg`, alt: 'Colorful planner and journal template grid on bright white background, organized pastel layout spreads' },
  { badge: 'Top Value', category: 'Content Creation', catClass: 'cat-content', title: '100,000+ HD Stock Photos', desc: '250+ categories, royalty-free, licensed for commercial use in ads, websites, social media, and print.', img: `${BASE_IMG}/100000_hd_stock_photos_bundle.jpg`, alt: 'Bright grid of diverse royalty-free photo thumbnails on white background showing various subjects and scenes' },
  { badge: 'Dev Favorite', category: 'Tech & Dev', catClass: 'cat-tech', title: '71 App Source Code Projects', desc: 'Android, Flutter, PHP, Laravel & React Native — each with full admin panels and source code ready to deploy.', img: `${BASE_IMG}/71_major_app_source_code_bundle.jpg`, alt: 'Clean mobile app UI screens on white background showing dashboard interfaces with bright modern design' },
  { badge: 'Massive Scale', category: 'Content Creation', catClass: 'cat-content', title: '300,000+ Motivation Quotes', desc: '100+ categories — a lifetime supply of social media content for coaches, educators, and content creators.', img: `${BASE_IMG}/300000_motivation_quotes_bundle.jpg`, alt: 'Bright colorful motivational quote cards on white background with bold typography and cheerful colors' },
  { badge: 'Unique Asset', category: 'Tech & Dev', catClass: 'cat-tech', title: '153 HTML5 Game Templates', desc: 'Web + Android WebView ready casual games — browser game templates ready to launch or customize.', img: `${BASE_IMG}/153_html5_game_mega_bundle.jpg`, alt: 'Colorful HTML5 game screenshots on bright background showing casual game interfaces with vibrant artwork' },
  { badge: 'Designer Pick', category: 'Design & Media', catClass: 'cat-design', title: '10,000+ Fonts, SVGs & Icons', desc: 'Commercial use design resources — Cricut SVG bundle, logo fonts, graphic elements, instant download.', img: `${BASE_IMG}/10000_fonts_svgs_icons_bundle_mega_pack.jpg`, alt: 'Colorful display of decorative fonts and icon sets on bright white background with various typography styles' },
];

export default function FeaturedSection() {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-20 lg:py-24 bg-section-dark" aria-labelledby="featured-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-14 reveal-hidden"
          ref={(el) => { itemRefs.current[0] = el; }}
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--secondary-foreground)' }}>
            Most Popular Resources
          </span>
          <h2 id="featured-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            The Standout Collections Inside
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Six collections that alone justify the price — and there are 37 more.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((item, i) => (
            <div
              key={i}
              ref={(el) => { itemRefs.current[i + 1] = el; }}
              className="reveal-hidden product-card group"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="card-img-wrapper relative">
                <img
                  src={item.img}
                  alt={item.alt}
                  loading="lazy"
                  width={600}
                  height={338}
                  className="w-full h-full object-cover bg-gray-800"
                />
                <span
                  className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, rgb(79,70,229), rgb(124,58,237) 40%, rgb(236,72,153))' }}
                >
                  {item.badge}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`category-pill ${item.catClass}`}>{item.category}</span>
                </div>
                <h3 className="font-bold text-base leading-tight mb-2" style={{ color: 'var(--foreground)' }}>{item.title}</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>{item.desc}</p>
                <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(30,41,59,0.4)' }}>
                  <span className="text-xs font-semibold" style={{ color: 'var(--secondary-foreground)' }}>✓ Included in Mega Bundle</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={16} height={16} style={{ color: 'var(--primary)' }}>
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="text-center mt-12 reveal-hidden"
          ref={(el) => { itemRefs.current[7] = el; }}
        >
          <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-base py-4 px-8 inline-flex">
            Get All 43 Collections — $79
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={18} height={18}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
          <p className="text-sm mt-3" style={{ color: 'var(--muted-foreground)' }}>One-time purchase • Instant digital access</p>
        </div>
      </div>
    </section>
  );
}
